











import {
  Vue,
  Component,
  Prop
} from "@/shared/vuepack";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useDynamsoftLicenseHelperStore } from "@/employee/store/dynamsoftLicenseHelper";

const DBR = (window as any).Capacitor ? (window as any).Capacitor.Plugins.DBR : undefined;

@Component({
  components: {
    GenericLoadingSpinner
  }
})
export default class BarcodeReaderV2 extends Vue {
  @Prop({ required: false })
  supportedFormats: string[];

  @Prop({ required: false })
  minLength: number;

  @Prop({ required: false })
  maxLength: number;

  dynamsoftLicenseHelperStore = useDynamsoftLicenseHelperStore();

  scannerResult = "";
  isLoading = true;
  frameReadListener;
  isScannerRunning = true;

  mounted () {
    this.dynamsoftLicenseHelperStore.initLicenseV2()
      .then(async result => {
        if (result.success === true) {
          try {
            await DBR.requestCameraPermission();
            const camerasResult = await DBR.getAllCameras();
            camerasResult.cameras.forEach(async camera => {
              const cameraID = camera;
              // USB camera
              // reference: https://github.com/tony-xlh/Quasar-QR-Code-Scanner/blob/main/src/components/QRCodeScanner.vue
              if (cameraID.toLowerCase().indexOf("founder") !== -1) {
                await DBR.selectCamera({ cameraID });
                return;
              }
            });
            this.startScan();
          } catch {
            this.close();
            (window as any).navigator.notification.alert("Please check your device settings to make sure camera permission is allowed for this app.", null, "Unable to access your camera");
            return;
          }
        }
      });
    document.addEventListener("pause", this.stopScan, false);
    document.addEventListener("resume", this.startScan, false);
  }

  async startScan () {
    this.isLoading = false;
    this.isScannerRunning = true;

    // set camera view to portrait mode
    (window as any).cordova.plugins.screenorientation.setOrientation("portrait");

    if (this.frameReadListener) {
      this.frameReadListener.remove();
    }

    this.frameReadListener = await DBR.addListener("onFrameRead", retObj => {
      this.onFrameRead(retObj);
    });

    await DBR.startScan();

    // since camera view is on the background, removing all the possible blockers
    (window as any).Capacitor.Plugins.StatusBar.hide();
    document.body.style.background = "none";
    document.getElementById("page-content-container").classList.remove("main");
    document.getElementById("footer").style.display = "none";
  }

  stopScan () {
    this.isScannerRunning = false;

    // reset screen orientation back to default
    (window as any).cordova.plugins.screenorientation.setOrientation("any");

    DBR.stopScan();

    // reset css changes
    (window as any).Capacitor.Plugins.StatusBar.show();
    document.getElementById("page-content-container").classList.add("main");
    document.getElementById("footer").style.display = "";

    if (this.frameReadListener) {
      this.frameReadListener.remove();
      this.removeScannerResult();
    }
  }

  onFrameRead (retObj) {
    const results = retObj.results;
    if (results && results.length > 0) {
      const barcodeFormat = results[0].barcodeFormat.trim().toUpperCase();
      const barcodeText = results[0].barcodeText.trim().toUpperCase();

      if (this.isValidBarcode(barcodeFormat, barcodeText)) {
        this.onValidBarcodeFound(barcodeText);
      } else {
        this.scannerResult = "Not supported. Please try another product.";
      }
    } else {
      this.scannerResult = "No barcode detected in this frame.";
    }
    this.updateScannerResult(this.scannerResult);
  }

  updateScannerResult (msg: string) {
    if (!this.isScannerRunning) {
      return false;
    }
    if (msg !== "" && msg.length > 0) {
      let element = document.getElementById("scannerResult");
      if (element) {
        element.innerHTML = msg;
        element.style.display = "block";
      } else {
        element = document.createElement("P");
        element.id = "scannerResult";
        element.setAttribute("style", "text-align:center;background-color:grey; color:white;bottom:10px");
        element.innerHTML = msg;
        document.body.appendChild(element);
      }
    }
  }

  isValidBarcode (format: string, code: string): boolean {
    if ((this.supportedFormats === undefined || this.supportedFormats.length === 0 ||
      this.supportedFormats.includes(format))
      && (this.minLength === undefined || !Number(this.minLength) || code.length === this.minLength
        || this.maxLength === undefined || !Number(this.maxLength) || code.length === this.maxLength)
    ) {
      return true;
    }
    return false;
  }

  onValidBarcodeFound (barcode: string) {
    this.close();
    this.$emit("success", barcode);
  }

  removeEventListeners () {
    document.removeEventListener("pause", this.stopScan);
    document.removeEventListener("resume", this.startScan);
  }

  close () {
    this.stopScan();
    this.$emit("close");
  }

  beforeDestroy () {
    this.removeEventListeners();
    this.removeScannerResult();
    this.stopScan();
  }

  removeScannerResult () {
    const element = document.getElementById("scannerResult");
    if (element) {
      element.style.display = "none";
    }
  }
}
